import { BooleanValue } from "../../../../components/ds2/BooleanValue";
import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  ModalDialog,
  Numeric,
  Switch,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { formatCoefficientOrDash } from "../model/coefficientFormat";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminGammaMValue, AdminStandard } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

type StandardOption = Pick<AdminStandard, "id" | "code" | "name">;

interface FormBody {
  standard: StandardOption | null;
  continuousCtrl: boolean | null;
  gammaM: number | null;
}

const AdminGammaMValuesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "gammaM" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useGammaMValues(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminGammaMValue>();
  const [itemToEdit, setItemToEdit] = useState<AdminGammaMValue>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Standard lookup map for display name ───
  const [stdMap, setStdMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const scAbort = new AbortController();
    adminApi
      .getStandards({ limit: 400, q: "" }, scAbort.signal)
      .then((items) => {
        const map: Record<string, string> = {};
        items.forEach((s) => {
          map[s.id] = `${s.code} - ${s.name}`;
        });
        setStdMap(map);
      })
      .catch(() => {});
    return () => {
      scAbort.abort();
    };
  }, []);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminGammaMValue>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}>
              <Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">
                {i18n.translation.lockCalculatorModule.admin.gammaMValues.standard}
              </TableHeadCell>
              <TableHeadCell align="left">
                {i18n.translation.lockCalculatorModule.admin.gammaMValues.continuousCtrl}
              </TableHeadCell>
              <TableHeadCell align="left">
                {i18n.translation.lockCalculatorModule.admin.gammaMValues.gammaM}
              </TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{stdMap[item.standardId] ?? item.standardId}</TableCell>
              <TableCell>
                {item.continuousCtrl === null ? (
                  i18n.translation.lockCalculatorModule.admin.gammaMValues.notApplicable
                ) : (
                  <BooleanValue value={item.continuousCtrl} />
                )}
              </TableCell>
              <TableCell>{formatCoefficientOrDash(item.gammaM)}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.gammaMValues.singular}
            isSubmitting={isSubmitting}
            buttons={[
              <Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>,
              <Button
                key={1}
                onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteGammaMValue(itemToDelete.standardId, itemToDelete.continuousCtrl)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}
              >
                {i18n.translation.common.yes}
              </Button>,
            ]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminGammaMValue; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const [defaultStandards, setDefaultStandards] = useState<StandardOption[]>([]);
  const aborter = useRef(new AbortController());

  useEffect(() => {
    adminApi
      .getStandards({ limit: 5, q: "" }, aborter.current.signal)
      .then(setDefaultStandards)
      .catch(() => setDefaultStandards([]));
  }, []);

  const loadStandards = async (inputValue: string): Promise<StandardOption[]> => {
    aborter.current.abort();
    aborter.current = new AbortController();
    return adminApi.getStandards({ limit: 5, q: inputValue }, aborter.current.signal);
  };

  const defaultValues: FormBody = {
    standard: item ? { id: item.standardId, code: item.standardCode ?? "", name: item.standardName ?? "" } : null,
    continuousCtrl: item ? item.continuousCtrl : false,
    gammaM: item?.gammaM ?? null,
  };

  const optionLabel = (x: { code: string; name: string }) => `${x.code} - ${x.name}`;

  const onSubmit = () => async (formData: FormBody) => {
    if (!formData.standard || formData.gammaM == null) return;
    try {
      if (item) {
        await adminApi.updateGammaMValue(item.standardId, item.continuousCtrl, { gammaM: formData.gammaM });
      } else {
        const body = {
          standardId: formData.standard.id,
          continuousCtrl: formData.continuousCtrl,
          gammaM: formData.gammaM,
        };
        await adminApi.insertGammaMValue(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.gammaMValues.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.gammaMValues.singular}`
      }
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}

        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="standard"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.gammaMValues.standard}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<StandardOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultStandards}
                      loadOptions={loadStandards}
                      isDisabled={!!item}
                      placeholder={i18n.translation.lockCalculatorModule.selectPlaceholder}
                      getOptionLabel={optionLabel}
                      getOptionValue={(x) => x.id}
                      isInvalid={fieldState.invalid}
                      value={defaultStandards.find((opt) => opt.id === field.value?.id) ?? field.value}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="continuousCtrl"
              control={control}
              render={({ field }) => {
                const isNotApplicable = field.value === null;
                return (
                  <>
                    <FormField label={i18n.translation.lockCalculatorModule.admin.gammaMValues.notApplicable}>
                      {({ labelId }) => (
                        <Switch
                          id={labelId}
                          checked={isNotApplicable}
                          onChange={(value) => field.onChange(value ? null : false)}
                        />
                      )}
                    </FormField>
                    {!isNotApplicable && (
                      <FormField label={i18n.translation.lockCalculatorModule.admin.gammaMValues.continuousCtrl}>
                        {({ labelId }) => (
                          <Switch
                            id={labelId}
                            checked={field.value ?? false}
                            onChange={(value) => field.onChange(value)}
                          />
                        )}
                      </FormField>
                    )}
                  </>
                );
              }}
            />
            <Controller
              name="gammaM"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.gammaMValues.gammaM}
                  error={fieldState.error}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={3}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminGammaMValuesTab;
